/* eslint-disable  @typescript-eslint/no-explicit-any */
import { EventEmitter, Injectable, RendererFactory2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from './data.service';
import { DomainService } from './domain.service';
import { MasterCardService, PaymentState } from './mastercard.service';
import { environment } from '../../environments/environment';
import { PayNowAtTableRequest, PayAtTableNiPayload } from '../models/pay-at-table.model';
import { MastercardPayload } from '../models/payment.model';
import { ApiService } from './api.service';
import { CartService } from './cart.service';
import { PaymentEventsService } from './payment-events.service';
// EzeTap and TotalProcessing gateways are not currently used by any venue — disabled below
// to skip them in the dialog-to-sheet conversion pass. Re-enable if a venue needs them again.
// import { EzetapComponent } from '../self-checkout/ezetap/ezetap.component';
// import { TotalProcessingComponent } from '../total-processing/total-processing.component';
import { AlertDialogComponent } from '../shared/alert-dialog/alert-dialog.component';
import { ToastService } from './toast.service';

export enum PaymentGateway {
  Payfort = 'payfort',
  RakBank = 'rak',
  Geidea = 'geidea',
  NI = 'network-intl',
  Mashreq = 'mashreq',
  NGenius = 'ngenius',
  EzeTap = 'ezetap',
  Credimax = 'credimax',
  TotalProcessing = 'totalprocessing',
  Arb = 'arb',
  CCAvenue = 'cc_avenue',
  BankMuscat = 'bank-muscat',
  Etisalat = 'etisalat',
  Magnati = 'magnati',
  Checkout = 'checkout',
  Checkout_V2 = 'checkout-v2',
  Noon = 'noon',
  Adyen = 'adyen',
  Cybersource = 'cybersource',
}

@Injectable({
  providedIn: 'root'
})
export class OnlinePaymentService {
  public paymentSheetClosed: EventEmitter<boolean> = new EventEmitter();

  private restaurantId: string;
  private defaultPG: string;
  private orderIds: string[];
  private payfortURL: string;
  private pendingPayload: MastercardPayload | PayAtTableNiPayload | null = null;

  // Gateways whose drop-in renders on its own dedicated route (see startRoutedGatewaySession).
  private readonly routedGateways: Partial<Record<string, { route: string; addRedirectUrl: boolean }>> = {
    [PaymentGateway.Geidea]: { route: '/payment/geidea', addRedirectUrl: false },
    [PaymentGateway.Adyen]: { route: '/payment/adyen', addRedirectUrl: true },
    [PaymentGateway.Checkout_V2]: { route: '/payment/checkout-v2', addRedirectUrl: true },
    // addRedirectUrl: the 3DS step-up returns by redirect even though the
    // challenge itself renders inside the widget.
    [PaymentGateway.Cybersource]: { route: '/payment/cybersource', addRedirectUrl: true },
  };

  constructor(
    private dataService: DataService,
    private apiService: ApiService,
    private mastercardService: MasterCardService,
    private domainService: DomainService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private rendererFactory: RendererFactory2,
    private router: Router,
    private cartService: CartService,
    private paymentEventsService: PaymentEventsService,
  ) {
    this.payfortURL = environment.urls.payfort;
    this.restaurantId = this.dataService.restaurant().id;
    this.setupPG();
  }

  private buildGatewayPayload(
    orderIds: string[],
    payNowRequest: PayNowAtTableRequest | null,
    tipAmount: number,
  ): MastercardPayload {
    const payload: MastercardPayload = {
      subdomain: this.domainService.getSubdomain(),
      order_ids: orderIds,
      tip_amount: tipAmount,
    };
    if (payNowRequest) {
      payload.payment_mode = "custom_amount";
      payload.cheque_id = payNowRequest.cheque_id;
      payload.payment_details = {
        amount: payNowRequest.total_amount,
      };
    }
    this.orderIds = orderIds;
    return payload;
  }

  public async startPayment(
    orderIds: string[],
    payNowRequest: PayNowAtTableRequest | null = null,
    tipAmount: number | 0 = 0,
  ): Promise<void> {
    // Cart clears later, once payment is confirmed (socket event or on-load reconciliation).
    this.cartService.setPendingPayment(orderIds);
    if (this.defaultPG === PaymentGateway.Payfort) {
      const payload: MastercardPayload = {
        subdomain: this.domainService.getSubdomain(),
        order_ids: orderIds,
        tip_amount: 0,
      };
      this.startPayfortSession(payload);
    } else if (this.defaultPG === PaymentGateway.RakBank || this.defaultPG === PaymentGateway.NI
      || this.defaultPG === PaymentGateway.Mashreq || this.defaultPG === PaymentGateway.Credimax) {
      const payload = this.buildGatewayPayload(orderIds, payNowRequest, tipAmount);

      // eslint-disable-next-line  @typescript-eslint/no-unused-vars
      this.mastercardService.setupScript(this.defaultPG).then(ok => {
        this.startMastercardSession(payload);
      });
    } else if (this.routedGateways[this.defaultPG]) {
      // Driven by the routedGateways map rather than a parallel list of keys:
      // adding a gateway to the map and forgetting to add it here means the
      // dispatch falls through every branch and the spinner never resolves.
      const payload = this.buildGatewayPayload(orderIds, payNowRequest, tipAmount);
      const { route, addRedirectUrl } = this.routedGateways[this.defaultPG]!;
      this.startRoutedGatewaySession(payload, route, addRedirectUrl);
    } else if (this.defaultPG === PaymentGateway.NGenius) {
      const payload: any = {
        order_ids: orderIds,
        subdomain: environment.production ? this.domainService.getSubdomain() : window.location.hostname,
      };
      this.apiService.getNGeniusSession(payload).subscribe(res => {
        window.location.href = res.payment_link;
      });
    // EzeTap and TotalProcessing gateways are not currently used by any venue — disabled to
    // skip them in the dialog-to-sheet conversion pass. Re-enable if a venue needs them again.
    // } else if (this.defaultPG === PaymentGateway.EzeTap) {
    //   const payload = {
    //     order_ids: orderIds,
    //     device_id: this.dataService.ezetapMachineId()
    //   };
    //   this.apiService.startEzetapSession(payload).subscribe(res => {
    //     this.dialog.open(EzetapComponent, { data: res, disableClose: true, width: '100%', height: '100%', maxWidth: '100vw', maxHeight: '100vh', panelClass: 'dialog-container' });
    //   });
    // } else if (this.defaultPG === PaymentGateway.TotalProcessing) {
    //   const payload: any = {
    //     order_ids: orderIds,
    //     payment_type: 'totalprocessing'
    //   };
    //   this.startTPSession(payload);
    } else if (this.defaultPG === PaymentGateway.Arb) {
      const payload = {
        order_ids: orderIds
      };
      this.apiService.getArbSession(payload).subscribe(res => {
        window.location.href = res.url;
      });
    } else if (this.defaultPG === PaymentGateway.CCAvenue || this.defaultPG === PaymentGateway.BankMuscat) {
      const payload = {
        order_ids: orderIds,
        subdomain: this.domainService.getSubdomain()
      };
      this.apiService.getCCAvenueSession(payload).subscribe(res => {
        const renderer = this.rendererFactory.createRenderer(null, null);
        const paymentForm = renderer.createElement('div');
        renderer.setProperty(paymentForm, 'innerHTML', res.toString());
        renderer.setStyle(paymentForm, 'width', '100vw');
        renderer.setStyle(paymentForm, 'height', '100%');
        renderer.setStyle(paymentForm, 'position', 'fixed');
        renderer.setStyle(paymentForm, 'top', '0');
        renderer.setStyle(paymentForm, 'left', '0');
        renderer.setStyle(paymentForm, 'z-index', '9999');
        renderer.setStyle(paymentForm, 'background', '#fff');
        document.body.appendChild(paymentForm);
        (<HTMLFormElement>document.getElementById("nonseamless")).submit();
      });
    } else if (this.defaultPG === PaymentGateway.Etisalat) {
      const payload = {
        order_ids: orderIds
      };
      this.apiService.getEtisalatSession(payload).subscribe(res => {
        if (res.url) {
          this.showPaymentLinkSentAlert();
        }
      });
    } else if (this.defaultPG === PaymentGateway.Magnati) {
      const payload = {
        order_ids: orderIds
      };
      this.apiService.getMagnatiSession(payload).subscribe(res => {
        if (res.url) {
          this.showPaymentLinkSentAlert();
        }
      });
    } else if (this.defaultPG === PaymentGateway.Checkout) {
      const payload = {
        subdomain: this.domainService.getSubdomain(),
        order_ids: orderIds
      };
      this.apiService.getCheckoutSession(payload).subscribe(res => {
        window.location.href = res.url;
      });
    } else if (this.defaultPG === PaymentGateway.Noon) {
      const payload = {
        subdomain: this.domainService.getSubdomain(),
        order_ids: orderIds
      };
      this.apiService.getNoonSession(payload).subscribe(res => {
        window.location.href = res.url;
      });
    }
  }

  private showPaymentLinkSentAlert(): void {
    AlertDialogComponent.open(this.dialog, {
      type: 'success',
      title: 'Payment Link Sent',
      message: 'The customer can now complete the payment using the link.',
      enableCancelBtn: false,
    }).afterClosed().subscribe(() => {
      this.cartService.clearCart();
      this.dialog.closeAll();
      this.router.navigate(['/menu-home']);
    });
  }

  public startPayAtTablePayment(payload: PayAtTableNiPayload, key: string): void {
    const routed = this.routedGateways[key];
    if (routed) {
      this.startRoutedGatewaySession(payload, routed.route, routed.addRedirectUrl);
      return;
    }

    this.mastercardService.setupScript(key).then(() => {
      this.startMastercardSession(payload);
    });
  }

  private setupPG(): void {
    if (!this.restaurantId) {
      return;
    }
    const methods = this.dataService.paymentMethods();
    const method = methods?.find(m => m.is_default_payment_gateway === true);
    if (!method) {
      return;
    } else {
      this.defaultPG = method.key;
    }
    // EzeTap gateway disabled — see note in startPayment(). Not calling checkEzetap()
    // so defaultPG never silently switches to a gateway startPayment() no longer handles.
    // this.checkEzetap();
  }

  // private checkEzetap(): void {
  //   const machineId = this.dataService.ezetapMachineId();
  //   if (machineId) {
  //     this.defaultPG = PaymentGateway.EzeTap;
  //   }
  // }

  private startMastercardSession(payload: MastercardPayload | PayAtTableNiPayload): void {
    this.mastercardService.getSession(payload)
      .subscribe((session) => {
        this.mastercardService.configureSession(session);
        this.mastercardService.showLightBox();
        this.mastercardService.paymentState.subscribe((state) => this.checkPaymentState(state));
      });
  }

  private startPayfortSession(payload: any): void {
    this.apiService.getPayfortSignature(this.restaurantId, payload).subscribe(signature => {
      const payfortForm = document.createElement('form');
      payfortForm.setAttribute('method', 'post');
      payfortForm.setAttribute('action', this.payfortURL);

      for (const key of Object.keys(signature)) {
        const input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', key);
        input.setAttribute('value', signature[key]);
        payfortForm.appendChild(input);
      }

      const serviceCommand = document.createElement('input');
      serviceCommand.setAttribute('type', 'hidden');
      serviceCommand.setAttribute('name', 'service_command');
      serviceCommand.setAttribute('value', 'TOKENIZATION');
      payfortForm.appendChild(serviceCommand);

      const lang = document.createElement('input');
      lang.setAttribute('type', 'hidden');
      lang.setAttribute('name', 'language');
      lang.setAttribute('value', 'en');
      payfortForm.appendChild(lang);

      document.body.appendChild(payfortForm);
      payfortForm.submit();
    });
  }

  // TotalProcessing gateway disabled — see note in startPayment(). Kept for reference in
  // case a venue needs this gateway again.
  // private startTPSession(payload: any): void {
  //   this.apiService.getTPCheckout(payload).subscribe(res => {
  //     const id = res.id;
  //     const checkoutScript = document.createElement('script');
  //     checkoutScript.src = `${environment.urls.totalprocessing}${id}`;
  //     checkoutScript.type = 'text/javascript';
  //     document.body.appendChild(checkoutScript);

  //     const total = res.orders.reduce((accumulator, order) => accumulator + order.total_price_without_discount, 0);
  //     const totalDiscountAmount = res.orders.reduce((accumulator, order) => accumulator + order.discount_amount, 0);
  //     const totalAmountWithDiscount = res.orders.reduce((accumulator, order) => accumulator + order.total_price, 0);

  //     checkoutScript.onload = () => {
  //       const data: TotalProcessingDetails = {
  //         total_price_without_discount: total,
  //         discount_amount: totalDiscountAmount,
  //         total_price: totalAmountWithDiscount,
  //         url: `${environment.urls.api}/payment/total_processing`
  //       };
  //       this.dialog.open(TotalProcessingComponent, { data, disableClose: true, width: '100%', height: '100%', maxWidth: '100vw', maxHeight: '100vh', panelClass: 'dialog-container' });
  //     };
  //   });
  // }

  private startRoutedGatewaySession(
    payload: MastercardPayload | PayAtTableNiPayload,
    route: string,
    addRedirectUrl: boolean,
  ): void {
    if (addRedirectUrl) {
      const tableId = this.dataService.table()?.id;
      payload.redirect_url = `${window.location.origin}/bill-paid?table_id=${tableId}`;
    }
    this.pendingPayload = payload;
    this.dialog.closeAll();
    this.router.navigate([route]);
  }

  public consumePendingPayload(): MastercardPayload | PayAtTableNiPayload | null {
    const payload = this.pendingPayload;
    this.pendingPayload = null;
    return payload;
  }

  private checkPaymentState(state: string): void {
    if (state === PaymentState.Cancelled) {
      this.paymentEventsService.notifyPaymentCancelled();
    } else if (state === PaymentState.Error) {
      this.toastService.error(this.translateService.instant('PAYMENT.ERROR'));
    }
  }
}
